import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import {
  signUpPasswordless,
  sendGoogleOAuthCode,
  googleOAuthRedirect,
  resetEmailError,
  setEmailInputToDanger,
  load as loadAuth,
} from 'redux/modules/auth';
import Panel from 'components/Panel/Panel';
import {
  ControlLabel,
  FormGroup,
  Form,
  Col,
  Button,
  HelpBlock,
} from 'react-bootstrap';
import { withRouter } from 'react-router';
import GoogleButton from 'components/Post/PostSelectRole/GoogleButton';
import Separator from 'components/Shared/Separator/Separator';
import Recaptcha from 'react-google-invisible-recaptcha';

import EmailInput from 'components/SignUp/EmailInput';
import AccountTypeInput from 'components/SignUp/AccountTypeInput.js';
import SvgIcon from 'components/SvgIcon/SvgIcon';
import styles from './Signup.module.scss';
import Loader from 'components/Loader/Loader';
import { trackEvent } from 'helpers/Analytics';
import { buildSignupAttributionUtm } from 'helpers/signupAttributionUtm';

@connect(
  (state) => ({
    emailError: state.auth.emailError,
    accountTypeError: state.auth.accountTypeError,
    emailValidationType: state.auth.emailValidationType,
    signedUpToGoogle: state.auth.signedUpToGoogle,
    signingUpViaGoogle: state.auth.signingUpViaGoogle,
    googleSignUpFailed: state.auth.googleSignUpFailed,
    signUpPasswordlessError: state.auth.signUpPasswordlessError,
    signUpPasswordlessLoading: state.auth.signUpPasswordlessLoading,
  }),
  {
    signUpPasswordless,
    loadAuth,
    setEmailInputToDanger,
    resetEmailError,
    googleOAuthRedirect,
    sendGoogleOAuthCode,
  }
)
@withRouter
class SignUp extends Component {
  static propTypes = {
    location: PropTypes.object,
    history: PropTypes.object,
    signUpPasswordless: PropTypes.func,
    loadAuth: PropTypes.func,
    emailError: PropTypes.array,
    accountTypeError: PropTypes.array,
    emailValidationType: PropTypes.string,
    setEmailInputToDanger: PropTypes.func,
    resetEmailError: PropTypes.func,
    googleOAuthRedirect: PropTypes.func,
    sendGoogleOAuthCode: PropTypes.func,
    signedUpToGoogle: PropTypes.bool,
    signingUpViaGoogle: PropTypes.bool,
    redirect: PropTypes.string,
    redirectFromGoogle: PropTypes.string,
    defaultAccountType: PropTypes.string,
    googleSignUpFailed: PropTypes.bool,
    signUpPasswordlessError: PropTypes.string,
    signUpPasswordlessLoading: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      email: '',
      type: props.defaultAccountType || '',
      errors: {},
      shouldRedirect: false,
      hasTriedToContinue: false,
      hide: false,
      showTypeError: false,
      disableSubmitButton: true,
      signingUp: false,
      toRender: null,
      isCollector: false,
      apiError: null,
    };
  }

  componentDidMount() {
    const { location } = this.props;
    trackEvent('user-lands-on-signup', { ucc: location.query.ucc || null });
    void this.handleGoogleOauthCode();
    if (location.query.email) {
      this.setState({
        email: location.query.email,
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      signedUpToGoogle,
      signUpPasswordlessError,
      signUpPasswordlessLoading,
    } = this.props;
    const { email, type } = this.state;
    const { email: prevEmail, type: prevType } = prevState;

    if (!prevProps.signedUpToGoogle && signedUpToGoogle) {
      this.loginAndRedirect();
    }

    // Handle API errors from Redux
    if (
      signUpPasswordlessError !== prevProps.signUpPasswordlessError &&
      signUpPasswordlessError
    ) {
      this.setState({ apiError: signUpPasswordlessError });
    }

    // Login Input change
    if (email !== prevEmail || type !== prevType) {
      let result = this.handleValidation();
      this.setState({ disableSubmitButton: !result });
    }

    if (signUpPasswordlessLoading !== prevProps.signUpPasswordlessLoading) {
      this.setState({ signingUp: signUpPasswordlessLoading });
    }
  }

  saveLocalUserType = () => {
    const { type } = this.state;
    try {
      localStorage.setItem('userType', JSON.stringify({ type }));
    } catch (error) {
      console.warn('Unable to save user type locally.', error.message);
    }
  };

  saveLocalAICollectorData = () => {
    const { isCollector, shouldRedirect } = this.state;
    try {
      localStorage.setItem('isCollector', JSON.stringify({ isCollector }));
      localStorage.setItem(
        'shouldRedirect',
        JSON.stringify({ shouldRedirect })
      );
    } catch (error) {
      console.warn(
        'Unable to save isCollector/ shouldRedirect locally.',
        error.message
      );
    }
  };

  retrieveLocalUserType = () => {
    let type = 'seller';
    try {
      type = JSON.parse(localStorage.getItem('userType')).type;
    } catch (error) {
      console.warn('Unable to retrieve local user type.', error.message);
    }
    return type;
  };

  retrieveLocalIsCollector = () => {
    if (!__CLIENT__) {
      return false;
    }
    let isCollector = false;
    try {
      isCollector = JSON.parse(localStorage.getItem('isCollector'))?.isCollector || false;
    } catch (error) {
      console.warn('Unable to retrieve local isCollector.', error.message);
    }
    return isCollector;
  };

  retrieveLocalShouldRedirect = () => {
    if (!__CLIENT__) {
      return false;
    }
    let shouldRedirect = false;
    try {
      shouldRedirect =
        JSON.parse(localStorage.getItem('shouldRedirect'))?.shouldRedirect ||
        false;
    } catch (error) {
      console.warn('Unable to retrieve local shouldRedirect.', error.message);
    }
    return shouldRedirect;
  };

  handleGoogleOauthCode = async () => {
    const googleOauthCode = this.isGoogleOAuthCode();
    if (googleOauthCode) {
      this.setState({ toRender: 'loader' });
      const { sendGoogleOAuthCode } = this.props;
      const type = this.retrieveLocalUserType();
      const redirect = 'signup';
      const aiCollector = this.retrieveLocalIsCollector();
      const utm = await buildSignupAttributionUtm();
      sendGoogleOAuthCode(googleOauthCode, redirect, type, aiCollector, utm);
    } else {
      this.setState({ toRender: 'signup' });
    }
  };

  isGoogleOAuthCode() {
    const urlString = window.location.href;
    const url = new URL(urlString);
    return url.searchParams.get('code');
  }

  resetHasTriedToContinue = () => {
    this.setState({
      hasTriedToContinue: false,
    });
  };

  handleSubmit = async (event) => {
    event.preventDefault();

    const utm = await buildSignupAttributionUtm();

    const signUpDetails = {
      email: this.state.email,
      type: this.state.type,
      utm,
      ai_collector: this.state.isCollector,
    };

    if (this.recaptcha) {
      this.setState({ signUpDetails, emailError: null }, () => {
        this.recaptcha.execute();
      });
    } else {
      this.handleValidationSignUpAndLogin(signUpDetails);
    }
  };

  handleValidationSignUpAndLogin = async (signUpDetails) => {
    trackEvent('signup-passwordless-attempt', { signUpDetails });
    if (
      this.props.emailValidationType === 'Invalid' &&
      !this.state.hasTriedToContinue
    ) {
      this.setState({
        hasTriedToContinue: true,
      });
      this.props.setEmailInputToDanger();
    } else {
      if (this.handleValidation()) {
        // Clear any previous API errors
        this.setState({ apiError: null });

        await this.props.signUpPasswordless(signUpDetails);

        // Check for API errors
        if (this.props.signUpPasswordlessError) {
          trackEvent('signup-passwordless-error', {
            error: this.props.signUpPasswordlessError,
          });
          this.setState({
            apiError: this.props.signUpPasswordlessError,
          });
          return;
        }

        // Only redirect if there are no validation errors and no API errors
        if (
          this.props.emailError.length === 0 &&
          this.props.accountTypeError.length === 0 &&
          !this.props.signUpPasswordlessError
        ) {
          this.redirectToAuthCode();
        }
      }
    }
  };

  handleRecaptchaResolved = async () => {
    const signUpDetails = {
      ...this.state.signUpDetails,
      recaptcha: this.recaptcha.getResponse(),
    };

    try {
      await this.handleValidationSignUpAndLogin(signUpDetails);
    } finally {
      // Reset reCAPTCHA after the submission attempt so it can be used again
      // (e.g. if validation fails and the user wants to resubmit).
      if (this.recaptcha) {
        this.recaptcha.reset();
      }

      this.setState({ signUpDetails: null });
    }
  };

  loginAndRedirect = async () => {
    await this.props.loadAuth();
    this.setState({ shouldRedirect: true });
  };

  redirectToAuthCode = () => {
    const { email } = this.state;
    trackEvent('signup-passwordless-success', {
      email,
    });
    // Encode email in base64
    const encodedEmail = btoa(email);
    this.props.history.push(`/auth/code?email=${encodedEmail}`);
  };

  typeValidation = () => {
    const { type } = this.state;
    const { accountTypeError, defaultAccountType } = this.props;

    if (type === '' && !defaultAccountType) {
      accountTypeError.length = 0;
      accountTypeError.push('Please select account type');
      return false;
    }

    accountTypeError.length = 0;
    return true;
  };

  emailValidation = () => {
    const { email } = this.state;
    const { emailError } = this.props;

    const handleEmailError = (errorMessage) => {
      emailError.length = 0;
      emailError.push(errorMessage);
    };

    if (email === '') {
      handleEmailError('Please enter your email address');
      return false;
    }

    if (!email.includes('@') || !email.includes('.')) {
      handleEmailError("Email doesn't contain @ or .");
      return false;
    }

    emailError.length = 0;
    return true;
  };

  handleValidation = () => {
    let result = true;

    result = this.typeValidation() && this.emailValidation();

    if (result === false) {
      this.setState(this.state);
    }
    return result;
  };

  handleEmailChange = (event) => {
    this.setState({
      email: event.target.value,
      hasTriedToContinue: false,
    });
    if (this.state.email.includes('@') && this.state.email.includes('.')) {
      this.props.resetEmailError();
    }
  };

  handleTypeChange = (event) => {
    event.target.value === 'collector'
      ? this.setState({ type: 'seller', isCollector: true })
      : this.setState({ type: event.target.value, isCollector: false });
  };

  handleGoogleButtonClick = (redirect) => {
    if (this.state.hide && this.state.type.length < 1) {
      this.props.accountTypeError.length = 0;
      this.props.accountTypeError.push('Please select account type');
      this.setState({ showTypeError: true });
      return;
    }

    if (this.state.type.length < 1) {
      this.setState({ hide: true });
      return;
    }

    this.saveLocalUserType();
    this.saveLocalAICollectorData();
    this.props.googleOAuthRedirect(redirect);
  };

  render() {
    const { shouldRedirect, isCollector, disableSubmitButton, signingUp } =
      this.state;
    const googleShouldRedirect = this.retrieveLocalShouldRedirect();
    const googleIsCollector = this.retrieveLocalIsCollector();
    const {
      emailError,
      history,
      redirectFromGoogle,
      accountTypeError,
      location,
      googleSignUpFailed,
    } = this.props;
    const type = location.query.type;

    const redirect = this.props.redirect ? this.props.redirect : '/';
    const defaultAccountType = this.props.defaultAccountType
      ? this.props.defaultAccountType
      : type
      ? type
      : null;

    if (shouldRedirect || googleShouldRedirect) {
      if (isCollector || googleIsCollector) {
        history.push(`/${redirect}/microprojects?type=collector`);
      } else {
        history.push(`/${redirect}`);
      }
    }

    return (
      <Panel className={styles.signUpPanel}>
        {this.state.toRender === 'loader' && !googleSignUpFailed && (
          <>
            <Loader loading={true} />
            <div> Google login succesful, redirecting... </div>{' '}
          </>
        )}
        {(this.state.toRender === 'signup' || googleSignUpFailed) && (
          <>
            <Form horizontal style={this.state.hide ? { display: 'none' } : {}}>
              <FormGroup controlId="signup-email">
                <Col sm={3} className={styles.formLabel}>
                  <div className={styles.labelContainer}>
                    <ControlLabel>Email address:</ControlLabel>
                  </div>
                </Col>
                <Col sm={9}>
                  <EmailInput
                    type="email"
                    placeholder="e.g. name@company.com"
                    styles={styles}
                    disabled={false}
                    value={this.state.email}
                    onChange={(event) => this.handleEmailChange(event)}
                    className={styles.formField}
                    isInPost={false}
                    resetContinue={this.resetHasTriedToContinue}
                    maxLength={255}
                  />
                  {emailError && emailError.length > 0 && (
                    <HelpBlock className={styles.helpBlock}>
                      {emailError}
                    </HelpBlock>
                  )}
                </Col>
              </FormGroup>
              <FormGroup key={1} controlId="signup-usertype">
                <Col sm={3} className={styles.formLabel}>
                  <div className={styles.labelContainer}>
                    <ControlLabel>Account type:</ControlLabel>
                  </div>
                </Col>
                <Col sm={9}>
                  <AccountTypeInput
                    onChange={(event) => this.handleTypeChange(event)}
                    accountTypeError={accountTypeError}
                    defaultAccountType={defaultAccountType}
                    styles={styles}
                  />
                </Col>
              </FormGroup>
              <p className={styles.termsText}>
                By signing up, you agree to our <a href="/terms">Terms</a>.
              </p>
              {this.state.apiError && (
                <FormGroup>
                  <HelpBlock
                    className={styles.helpBlock}
                    style={{ color: 'red', textAlign: 'center' }}
                  >
                    {this.state.apiError}
                  </HelpBlock>
                </FormGroup>
              )}
              <FormGroup>
                <div className={styles.signUpContainer}>
                  <Button
                    className={`${styles.signUpButton} lato-bold`}
                    bsStyle="primary"
                    bsSize="lg"
                    type="submit"
                    disabled={disableSubmitButton || signingUp}
                    onClick={(event) => this.handleSubmit(event)}
                  >
                    <div className={styles.signUpCTA}>
                      <span className={styles.signUpCopy}>
                        {signingUp ? 'Signing up...' : 'Sign up'}
                      </span>
                      {!signingUp && <SvgIcon icon="chevron-right" size="14" />}
                    </div>
                  </Button>
                </div>
              </FormGroup>
            </Form>
            <div style={this.state.hide ? { display: 'none' } : {}}>
              <Separator text="OR" />
            </div>
            <br />
            <Form horizontal style={this.state.hide ? {} : { display: 'none' }}>
              <FormGroup controlId="signup-usertype2">
                <Col sm={3} className={styles.formLabel}>
                  <ControlLabel>Account type:</ControlLabel>
                </Col>
                <Col sm={9}>
                  <AccountTypeInput
                    onChange={(event) => this.handleTypeChange(event)}
                    accountTypeError={accountTypeError}
                    styles={styles}
                  />
                </Col>
              </FormGroup>
              <br />
            </Form>
            <div>
              <GoogleButton
                googleOAuthRedirect={this.handleGoogleButtonClick}
                redirect={redirectFromGoogle}
              />
            </div>
            <br />
            <hr />
            <div style={{ textAlign: 'center' }}>
              <span>
                Already have an account? <a href="/signin">Sign in</a>
              </span>
            </div>
            {process.env.GOOGLE_RECAPTCHA_API_KEY && (
              <Recaptcha
                ref={(ref) => (this.recaptcha = ref)}
                sitekey={process.env.GOOGLE_RECAPTCHA_API_KEY}
                onResolved={this.handleRecaptchaResolved}
              />
            )}
          </>
        )}
      </Panel>
    );
  }
}

export default SignUp;
